import { Component, Input, forwardRef, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DropdownOption<T = unknown> {
  label: string;
  value: T;
}

@Component({
  selector: 'slotcom-dropdown',
  standalone: true,
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent<T = unknown> implements ControlValueAccessor {
  @Input() label = '';
  @Input() options: DropdownOption<T>[] = [];
  @Input() defaultLabel!: DropdownOption<T>;

  value = signal<T | null>(null);
  isOpen = signal(false);

  selectedLabel = computed(() => {
    const value = this.value();

    const option = this.options.find((o) => o.value === value);

    return option?.label ?? this.defaultLabel.label;
  });

  private onChange = (value: T | null) => {
    void value;
  };

  private onTouched: () => void = () => {};

  toggle() {
    this.isOpen.update((v) => !v);
  }

  select(option: DropdownOption<T>) {
    this.value.set(option.value);

    this.onChange(option.value);
    this.onTouched();

    this.isOpen.set(false);
  }

  isSelected(option: DropdownOption<T>): boolean {
    return option.value === this.value();
  }

  writeValue(value: T | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
